import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Sparkles,
  Camera,
  Video,
  Music,
  Heart,
  Crown,
  Gem,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { SITE } from "../../lib/site";

const FULL_URL = "https://www.asanimation61.com/votre-mariage";

const KEY_PACKAGES = [
  {
    id: "elegance",
    name: "Élégance",
    icon: <Heart size={20} />,
    accent: "from-[#D4AF37]/30 to-transparent",
    sono: [
      "Sonorisation cocktail (1 enceinte sur batterie)",
      "Repas & Dancefloor (2 enceintes)",
      "2 micros sans fil",
      "Éclairage : 8 projecteurs + 4 robotisés",
      "Vidéoprojecteur · Écran 2 × 2 m",
    ],
    media: [
      "25 photos de couple pro",
      "Photos de groupes & mini-groupes",
      "Album HD 30 × 30 luxe",
    ],
  },
  {
    id: "prestige-plus",
    name: "Prestige +",
    icon: <Gem size={20} />,
    accent: "from-[#2D6CFF]/30 to-transparent",
    featured: true,
    sono: [
      "Sonorisation cocktail + repas + dancefloor",
      "2 micros sans fil",
      "Éclairage : 14 projecteurs + 8 robotisés",
      "Fumée lourde au sol",
      "Vidéoprojecteur · Écran 3 × 3 m",
      "Film HD offert de votre soirée",
    ],
    media: [
      "35 photos de couple pro",
      "Reportage photo journée entière",
      "+1000 photos sur USB",
      "Album HD 30 × 30 luxe",
    ],
  },
  {
    id: "royal",
    name: "Royal",
    icon: <Crown size={20} />,
    accent: "from-[#D4AF37]/40 to-[#FFD700]/10",
    sono: [
      "Sono cocktail + cérémonie laïque + VH (2 enceintes batterie)",
      "Repas & Dancefloor (2 enceintes)",
      "2 micros sans fil",
      "Éclairage : 14 projecteurs + 10 robotisés",
      "Étincelles froides + fumée lourde",
      "Vidéoprojecteur · Écran 3 × 3 m · Film HD offert",
    ],
    media: [
      "40 photos de couple pro",
      "Reportage photo journée entière",
      "+2500 photos sur USB",
      "Album HD 30 × 30 luxe",
      "Film vidéo HD de la journée",
    ],
  },
];

const SONO_PACKAGES = [
  { id: "love", name: "Love", desc: "DJ + Sono cocktail/repas/dancefloor + éclairage + vidéo." },
  { id: "prestige", name: "Prestige", desc: "+ 14 projecteurs, fumée lourde, écran 3×3m, film HD offert." },
  { id: "gold", name: "Gold", desc: "+ Cérémonie laïque sonorisée, étincelles froides, set premium." },
];

export const WeddingPackages = () => {
  const [tab, setTab] = useState("key");

  return (
    <section
      id="forfaits-mariage"
      data-testid="wedding-packages-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--bg-surface)] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.14),transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="eyebrow inline-flex items-center gap-2">
            <Sparkles size={13} /> Mariage · Forfaits complets
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            DJ + Sono + Photographe + Vidéaste,
            <br />
            <span className="gold-text-gradient">en un seul forfait.</span>
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            Pour votre mariage, nous proposons des forfaits{" "}
            <span className="text-white font-semibold">clés en main</span> qui réunissent
            tous les prestataires essentiels : un seul interlocuteur, une coordination
            parfaite, zéro mauvaise surprise.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-10">
          <div
            className="inline-flex p-1.5 rounded-full glass border border-white/10"
            data-testid="wedding-tabs"
          >
            <button
              onClick={() => setTab("key")}
              data-testid="tab-key"
              className={`px-5 md:px-7 py-3 text-xs md:text-sm font-semibold rounded-full transition-all duration-300 ${
                tab === "key"
                  ? "bg-gradient-to-r from-[#E9C766] to-[#B8862C] text-black shadow-lg"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              Clés en main · DJ + Photo + Vidéo
            </button>
            <button
              onClick={() => setTab("sono")}
              data-testid="tab-sono"
              className={`px-5 md:px-7 py-3 text-xs md:text-sm font-semibold rounded-full transition-all duration-300 ${
                tab === "sono"
                  ? "bg-gradient-to-r from-[#E9C766] to-[#B8862C] text-black shadow-lg"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              DJ + Sonorisation
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {tab === "key" ? (
            <motion.div
              key="key"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
            >
              {KEY_PACKAGES.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  data-testid={`wedding-key-${p.id}`}
                  className={`relative rounded-2xl overflow-hidden border ${
                    p.featured
                      ? "border-[#D4AF37]/50 gold-glow"
                      : "border-white/10"
                  } bg-[#0A0A0A] hover:-translate-y-1 transition-all duration-300 flex flex-col`}
                >
                  {/* Top gradient */}
                  <div className={`relative h-2 bg-gradient-to-r ${p.accent}`} />

                  {p.featured && (
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 rounded-full text-[10px] tracking-[0.22em] uppercase font-semibold bg-[#D4AF37] text-black">
                        Le + choisi
                      </span>
                    </div>
                  )}

                  <div className="p-7 md:p-8 flex flex-col flex-1">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] mb-5">
                      {p.icon}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                      {p.name}
                    </h3>
                    <div className="mt-1.5 text-xs uppercase tracking-[0.22em] text-zinc-500">
                      Forfait clés en main
                    </div>

                    <div className="mt-7 space-y-5 flex-1">
                      <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3">
                          <Music size={13} /> Sonorisation & DJ
                        </div>
                        <ul className="space-y-2">
                          {p.sono.map((item) => (
                            <li
                              key={item}
                              className="flex items-start gap-2.5 text-sm text-zinc-300 leading-snug"
                            >
                              <Check
                                size={15}
                                className="text-[#D4AF37] shrink-0 mt-0.5"
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="pt-5 border-t border-white/5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A86FF] font-semibold mb-3">
                          <Camera size={13} /> Photo & Vidéo
                        </div>
                        <ul className="space-y-2">
                          {p.media.map((item) => (
                            <li
                              key={item}
                              className="flex items-start gap-2.5 text-sm text-zinc-300 leading-snug"
                            >
                              <Check
                                size={15}
                                className="text-[#4A86FF] shrink-0 mt-0.5"
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <a
                      href="#contact"
                      data-testid={`wedding-cta-${p.id}`}
                      className={`mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-all ${
                        p.featured
                          ? "btn-gold"
                          : "btn-outline-gold"
                      }`}
                    >
                      Demander un devis <ArrowRight size={15} />
                    </a>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="sono"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
            >
              {SONO_PACKAGES.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  data-testid={`wedding-sono-${p.id}`}
                  className="glass rounded-2xl p-7 md:p-8 hover:-translate-y-1 hover:border-[#D4AF37]/30 transition-all duration-300 flex flex-col"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] mb-5">
                    <Music size={20} />
                  </div>
                  <h3 className="text-2xl font-bold text-white">{p.name}</h3>
                  <div className="mt-1.5 text-xs uppercase tracking-[0.22em] text-zinc-500">
                    DJ + Sonorisation
                  </div>
                  <p className="mt-5 text-sm text-zinc-400 leading-relaxed flex-1">
                    {p.desc}
                  </p>
                  <a
                    href="#contact"
                    data-testid={`wedding-sono-cta-${p.id}`}
                    className="mt-7 btn-outline-gold w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm"
                  >
                    Demander un devis <ArrowRight size={15} />
                  </a>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-14 flex flex-col md:flex-row items-center justify-center gap-4 text-center">
          <a
            href={FULL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wedding-full-page-btn"
            className="text-sm text-zinc-300 hover:text-[#D4AF37] inline-flex items-center gap-2 underline-offset-4 hover:underline"
          >
            Voir tous les détails et options sur asanimation61.com
            <ExternalLink size={14} />
          </a>
          <span className="hidden md:inline text-zinc-600">·</span>
          <a
            href={`tel:${SITE.phoneInternational}`}
            data-testid="wedding-call-btn"
            className="text-sm text-[#D4AF37] hover:underline underline-offset-4"
          >
            Ou appelez le {SITE.phoneDisplay}
          </a>
        </div>

        {/* Add-ons strip */}
        <div className="mt-12 glass rounded-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <div className="text-xs tracking-[0.22em] uppercase text-[#D4AF37] font-semibold mb-2">
                Options & animations
              </div>
              <div className="text-white font-semibold text-base md:text-lg">
                Étincelles froides · Fumée lourde · Photobooth · Caricaturiste · Magicien · Mentaliste · Ténor · Décoration LED de salle
              </div>
            </div>
            <a
              href="#contact"
              data-testid="wedding-options-cta"
              className="btn-gold rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 shrink-0"
            >
              Composer mon mariage <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
